import { useToast, Box, Flex, Heading, Image, Text, VStack, useDisclosure, useMediaQuery } from '@chakra-ui/react'
import ChatFooter from '../components/chatfooter'
import Messages from '../components/messages';
import { useCallback, useEffect, useRef, useState } from 'react';
import fetchWrapper from '../lib/fetch-wrapper';
import { v4 as uuidv4 } from 'uuid';
import Disclaimer from '../components/disclaimer';
import { url } from '../lib/constants'
import SideBar from "../components/sidebar";
import BlockingOverlay from '../components/blockingoverrelay';
import { useProfile } from '../lib/profileContext';
import { useTranslation } from 'react-i18next';
import FeedbackModal from '../components/feedbackLoop/feedbackModal';
import SideBarMobile from '../components/sideBarMobile';

export default function Chat() {
    const { t, i18n } = useTranslation();
    const defaultmessage = { role: "assistant", message: t('welcome_message') };
    const [messages, setMessages] = useState<any[]>([defaultmessage]);
    const [inputMessage, setInputMessage] = useState("");
    const [aiResponseInprogress, setAiResponseInprogress] = useState(false);
    const [sessionId, setSessionId] = useState<any>(null)
    const [enableChat, setEnableChat] = useState(true)
    const [loginUserName, setLoginUserName] = useState<any>(null)
    const [isAgreedTerms, setIsAgreedTerms] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isPowerBIViewer, setIsPowerBIViewer] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [feedbackMessageId, setFeedbackMessageId] = useState<string>();
    const [llmUnavailable, setLlmUnavailable] = useState(false);
    const isMobile = useMediaQuery("(max-width: 768px)")[0];
    const ref = useRef<HTMLTextAreaElement>(null);
    const toast = useToast();
    const { profile } = useProfile();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    useEffect(() => {
        const fw = new fetchWrapper();

        const checkChatStatus = async () => {
            try {
                const res = await fw.get('/api/chat-status');
                setLlmUnavailable(!res.data.chat_status);
            } catch (error) {
                console.error("Failed to check LLM status", error);
                setLlmUnavailable(true);
            }
        };

        void checkChatStatus();

        const interval = setInterval(checkChatStatus, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const { isOpen: blocking, onOpen, onClose } = useDisclosure();

    const targetRef = useRef<any>();

    const downloadChat = async () => {
        onOpen()
        await targetRef.current?.exportPdf()
        onClose()
    }

    useEffect(() => {
        ref?.current?.focus();
        void createSessionIfNotExists()
        i18n.on('languageChanged', () => {
            setMessages([{ role: "assistant", message: t('welcome_message') }, ...messages.slice(1)]);
        })
    }, [])

    const callChatAPI = async (data: any, regenerate = false) => {

        try {

            const fw = new fetchWrapper();
            setAiResponseInprogress(true)
            const res = await fw.post(
                `${url}/api/chat`,
                { ...data,
                    regenerate: regenerate,
                    systemMessage: '',
                    lng: i18n.language
                }
            );
            const respMessage = res.data;

            if (res.status === 200) {
               setMessages((old: any[]) => [...old, { messageId: 'asd', role: "assistant", ...respMessage }]);
            }
            else {
                setMessages((old: any[]) => [...old, {  messageId: 'asd', role: "assistant", message: respMessage.Message }]);
            }
            setAiResponseInprogress(false)
        } catch (err) {
            setMessages((old: any[]) => [...old, { role: "assistant", message: t('error_occured') }]);
            setAiResponseInprogress(false)
        }
    }

    const createSessionIfNotExists = async () => {
        if (!sessionId) {
            try {

                const fw = new fetchWrapper();

                let newSessionId: string | null = `${uuidv4()}`;

                const res = await fw.post(`${url}/api/session`, { sessionId: newSessionId });

                if (res.status === 200) {
                    const respJson = res.data;
                    setLoginUserName(profile ? profile.firstName : '')
                    setIsAdmin(respJson.isAdmin)
                    setIsPowerBIViewer(respJson.isPowerBI)
                    setSessionId(newSessionId)
                }
                else {
                    setSessionId(null)
                    setIsAdmin(false)
                    setIsPowerBIViewer(false)
                    newSessionId = null;
                }

                return newSessionId
            } catch (err) {
                setSessionId(null)
                setIsAdmin(false)
                setIsPowerBIViewer(false)
                setAiResponseInprogress(false)

                return null;
            }
        }
        else {
            return sessionId;
        }
    }

    const handleSendMessage = async () => {
        if (!inputMessage.trim().length) {
            return;
        }

        const currentSessionId = await createSessionIfNotExists()
        if (currentSessionId) {
            const data = { role: "user", message: inputMessage, sessionId: currentSessionId, messageId: `${uuidv4()}` };
            setMessages((old) => [...old, data]);
            setInputMessage("");
            await callChatAPI(data)
        } else {
            toast({
                title: t('session_error_title'),
                description: t('session_error'),
                status: 'error',
                position: 'bottom',
                duration: 5000,
                isClosable: true,
            });
        }

    };

    const handleClearChat = () => {
        setInputMessage("");
        setSessionId(null)
        setMessages([defaultmessage]);
        setEnableChat(true)
    }

    const handleInputMessage = (message: string) => {
        if (message.length <= 500) {
            setInputMessage(message);
        } else {
            setInputMessage(message.substring(0, 500))
        }
    }

    const termsAndConditionsHandler = (agreed: boolean) => {
        setIsAgreedTerms(agreed);
    }

    const messageFeedbackHandler = useCallback(async (messageId: string, isLiked: Boolean | null, isUpdate: Boolean = false) => {
        const fw = new fetchWrapper();
        let res = null
        if (!isUpdate) {
            const isLast = messages.length - 1 === findLastIndex(messages, (message: any) => message.messageId === messageId);
            if (isLiked === false && isLast) {
                setFeedbackMessageId(messageId);
                setIsFeedbackModalOpen(true);
                return;
            } else {
                res = await fw.post(`${url}/api/feedback`, { messageId: messageId, isLiked: isLiked });
            }
        }
        else {
            res = await fw.put(`${url}/api/feedback`, { messageId: messageId, isLiked: isLiked });
        }

        if (res && res.status === 200) {
            setMessages((messages) => {
                return messages.map((obj) => (
                    obj.messageId === messageId && obj.role === 'assistant' ? { ...obj, isLiked: isLiked, submittedFeedback: true } : obj
                ))
            })
        }

        function findLastIndex (arr: any, predicate: any) {
            for (let i = arr.length - 1; i >= 0; i--) {
                if (predicate(arr[i])) {
                    return i;
                }
            }
            return -1;
        }
    }, [messages])

    const messageRegenerateHandler = useCallback(async (messageId: string) => {
        if (messageId) {
            let message = null;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'user') {
                    message = messages[i];
                    break;
                }
            }
            message.regenerate = true;
            message.messageId = `${uuidv4()}`;
            console.log(message);

            removeLastItem();
            await callChatAPI(message, true);
        } else {
            removeLastItem();
        }

        function removeLastItem() {
            setMessages((old: any[]) => {
                const newArr = [...old];
                newArr.pop();

                return newArr;
            });
        }
    }, [messages]);

    const activityClickHandler = async (sessionId: string) => {
        try {
            setEnableChat(false)
            const fw = new fetchWrapper();

            const res = await fw.get(`${url}/api/session/${sessionId}`);

            if (res.status === 200) {
                let messages = res.data;
                messages.unshift(defaultmessage)
                setMessages(messages);
            }
            else {
                handleClearChat()
            }
        } catch (err) {
            handleClearChat()
        }
    }

    const feedbackCloseHandler = async (data: any) => {
        const fw = new fetchWrapper();
        setIsFeedbackModalOpen(false);
        if (data) {
            setMessages((old: any[]) => [...old, {
                role: 'assistant',
                message: t('regenerate_message'),
                messageId: `${uuidv4()}`,
                sessionId: sessionId,
                reg: true,
            }]);
        }

        const res = await fw.post(`${url}/api/feedback`, { messageId: feedbackMessageId, isLiked: false, comment: data });
        if (res.status === 200) {
            setMessages((messages) => {
                return messages.map((obj) => (
                    obj.messageId === feedbackMessageId && obj.role === 'assistant' ? { ...obj, isLiked: false, submittedFeedback: true } : obj
                ))
            })
        }

        setFeedbackMessageId('');
    };

    return (<>{blocking && <BlockingOverlay />}
        <Flex id="app-content" h={'100dvh'} backgroundColor="#ffffff">
            <Box bg={'brand.900'} h="100dvh" p={isMobile ? 0 : 5}>
            {isAgreedTerms && (<>
                {isMobile ? (
                    <SideBarMobile isPowerBIViewer={isPowerBIViewer} isAdmin={isAdmin} onClearChat={handleClearChat} onActivityClick={activityClickHandler} />
                ) : (<SideBar isPowerBIViewer={isPowerBIViewer} isAdmin={isAdmin} onClearChat={handleClearChat} onActivityClick={activityClickHandler} />)}
            </>)}
            </Box>
            <Box flex={1} px={5} position="relative">
                {llmUnavailable && (
                    <Box
                        position="absolute"
                        top={!isMobile ? '0' : ''}
                        bottom={isMobile ? '0' : ''}
                        left="0"
                        right="0"
                        bg="red.500"
                        color="white"
                        textAlign="center"
                        py={1}
                        px={3}
                        fontSize="sm"
                        fontWeight="semibold"
                        zIndex="999"
                    >
                        ⚠️ The language model is currently experiencing high load. Responses may be delayed or unavailable.
                    </Box>
                )}
                <Box>
                    <Flex direction={'row'} gap={3} mt='5px'>
                        {isMobile ? (
                            <Heading size={'xl'} pt={2} pl={50} textAlign='center' color={'brand.900'} fontWeight={'450'}>AskHR</Heading>
                        ) : (
                            <>
                                <Image alt="Wind Creek Hospitality" padding="12px 36px" src="/logo.png" mt="0px" width="auto" height="100px" />
                                <Heading size={'xl'} pt={30} textAlign='center' color={'brand.900'} fontWeight={'450'}>AskHR</Heading>
                            </>
                        )}
                    </Flex>
                </Box>
                <Box h={isMobile ? 'calc(100dvh - 160px)' : 'calc(100dvh - 205px)'} overflowY={'auto'}>
                    {isAgreedTerms && <>
                        <Box maxW={'1100px'} margin={'auto'}>
                            <Messages
                                ref={targetRef}
                                loginUserName={loginUserName}
                                isEnabled={enableChat}
                                messages={messages}
                                messageFeedbackClick={messageFeedbackHandler}
                                OnMessageRegenerateClick={messageRegenerateHandler}
                                aiResponseInprogress={aiResponseInprogress}
                            />
                        </Box>
                    </>
                    }
                    {!isAgreedTerms && <Text fontSize={20} mt={10}>{t('please_review_disclaimer')}</Text>}
                    <div ref={messagesEndRef}/>
                </Box>
                {isAgreedTerms &&
                    <Box maxW={'1100px'} margin={'auto'} p={3}>
                            <Flex gap={3}>
                                <VStack flex={'1'}>
                                    <ChatFooter isEnabled={enableChat} aiResponseInprogress={aiResponseInprogress}
                                                inputMessage={inputMessage} handleSendMessage={handleSendMessage}
                                                setInputMessage={handleInputMessage} inputRef={ref}
                                    ></ChatFooter>
                                    <Text color={'brand.900'} fontStyle={'italic'} fontWeight={'600'}>{t('check_for_mistakes')}</Text>
                                </VStack>
                            </Flex>
                    </Box>
                }
            </Box>
            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={(data: any) => feedbackCloseHandler(data)}
            />


            <Disclaimer agreed={termsAndConditionsHandler} />
        </Flex>
    </>
    )
}